import { Button } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { Form } from 'components/organisms';
import {
  musicProductPhysicalInitialValues,
  musicProductPhysicalTabs,
} from 'components/organisms/forms/music-product-physical/MusicProductPhysicalForm';
import { useHasPermission } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiCopy, PiPencilSimpleLine } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { usePostMusicProductDigtialFromPhysical } from 'services/music-product-digital';
import {
  useDeleteMusicProductPhysical,
  useGetMusicProductPhysical,
} from 'services/music-product-physical';
import { STATUS } from 'services/types';
import { getMainTitle } from 'utils/global/mainTitle.utils';
import { getLastTabPathname } from 'utils/navbar/getParentPathname';
import {
  onErrorShowAllErrorsToast,
  onErrorShowToast,
} from 'utils/service/errorHandler';

const MusicProductPhysical = () => {
  const musicProductPhysicalId = useParamId('music-product-physical');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const hasPermission = useHasPermission();

  const { t } = useTranslation();

  const { data: musicProductPhysical, isLoading } = useGetMusicProductPhysical(
    musicProductPhysicalId
  );

  const removeMusicProductPhysical = useDeleteMusicProductPhysical(
    musicProductPhysicalId
  );

  const musicProductPhysicalIndividualInitialValues = musicProductPhysical
    ? middleware.musicProductPhysical.queryToForm(musicProductPhysical)
    : musicProductPhysicalInitialValues;

  const goToEditMusicProductPhysical = () => {
    musicProductPhysical &&
      navigate(
        `/music-product-physical/edit/${musicProductPhysicalId}/${getLastTabPathname(
          pathname
        )}`,
        {
          replace: true,
        }
      );
  };

  const onRemoveMusicProductPhysical = async (actions: PopupActions) => {
    await removeMusicProductPhysical.mutateAsync(undefined, {
      onError: error => {
        actions.close();
        onErrorShowToast(error);
      },
      onSuccess: () => navigate('/music-product-physical'),
    });
  };

  const createDigital = usePostMusicProductDigtialFromPhysical();
  const onCreateMusicProductDigital = async () =>
    await createDigital.mutateAsync(musicProductPhysicalId, {
      onSuccess: physicalId =>
        navigate(`/music-product-digital/${physicalId}/tracks`),
      onError: error => onErrorShowAllErrorsToast(error),
    });

  return (
    <Form.MusicProductPhysical
      i18nextPrefix='music-product-physical'
      onSubmit={() => {}}
      initialValues={musicProductPhysicalIndividualInitialValues}
      tittle={getMainTitle(musicProductPhysical?.mainTitle)}
      disableTabs={
        musicProductPhysical?.releaseStatus === STATUS.draft
          ? [musicProductPhysicalTabs.statistics]
          : []
      }
      backPath='music-product-physical'
      status={{ displayMode: true, isLoading }}
      topbarContent={
        <div className='flex'>
          {hasPermission('PhysicalMusicProductPermissionsLIST**', 1) && (
            <Button
              isClear
              onlyIcon
              onClick={onCreateMusicProductDigital}
              isLoading={createDigital.isPending}>
              <Button.Description description={t('button.duplicate')} />
              <Button.Icon icon={<PiCopy />} />
              <Button.Text text={t('music-product-physical.create-digital')} />
            </Button>
          )}
          {hasPermission('PhysicalMusicProductPermissionsLIST**', 2) && (
            <Button isClear onlyIcon onClick={goToEditMusicProductPhysical}>
              <Button.Description description={t('button.edit')} />

              <Button.Icon icon={<PiPencilSimpleLine />} />
            </Button>
          )}
          {hasPermission('PhysicalMusicProductPermissionsLIST**', 4) && (
            <MagellanPopups.DeleteWarning
              name={musicProductPhysical?.mainTitle.title}
              onRemove={onRemoveMusicProductPhysical}
            />
          )}
        </div>
      }
    />
  );
};

export default MusicProductPhysical;
